import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

/**
 * Custom Zod JSON schema
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const json = <TData extends object = any>(options: { optional?: boolean } = {}) =>
  z.string().transform((content, ctx) => {
    if (!content && options.optional) return null;
    try {
      return JSON.parse(content) as TData;
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid JSON',
      });
      return z.never;
    }
  });

export const env = createEnv({
  server: {
    ROLLBAR_SERVER_TOKEN: z.string().optional(),
    FIREBASE_ADMIN_CREDENTIAL: json({ optional: true }),
    FIREBASE_PRIVATE_KEY: z.string().optional(),
    GA4_PROPERTY_ID: z.string().optional(),
    GA4_APP_STREAM_IDS: z.string().optional(),
    GA4_WEB_STREAM_IDS: z.string().optional(),
  },

  client: {
    NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN: z.string().optional(),
    NEXT_PUBLIC_ROLLBAR_TIME_WINDOW_MIN: z.number().optional(),
    NEXT_PUBLIC_ROLLBAR_ENV: z.string().optional(),
    NEXT_PUBLIC_FIREBASE_CONFIG: json(),
    NEXT_PUBLIC_FIREBASE_MESSAGING_VAPID_KEY: z.string().min(1),
    NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: z.string().optional(),
    NEXT_PUBLIC_URL: z.string().optional(),
    NEXT_PUBLIC_GA_TRACKING_ID: z.string().optional(),
    NEXT_PUBLIC_GOOGLE_ANALYTICS: z.string().optional(),
    NEXT_PUBLIC_FACEBOOK_PIXEL_ID: z.string().optional(),
    NEXT_PUBLIC_FORCE_SUBDOMAIN: z.string().optional(),
    NEXT_PUBLIC_API_REST_BASE_URL: z.string().optional(),
  },

  runtimeEnv: {
    ROLLBAR_SERVER_TOKEN: process.env.ROLLBAR_SERVER_TOKEN,
    FIREBASE_ADMIN_CREDENTIAL: process.env.FIREBASE_ADMIN_CREDENTIAL,
    FIREBASE_PRIVATE_KEY: process.env.FIREBASE_PRIVATE_KEY,
    GA4_PROPERTY_ID: process.env.GA4_PROPERTY_ID,
    GA4_APP_STREAM_IDS: process.env.GA4_APP_STREAM_IDS,
    GA4_WEB_STREAM_IDS: process.env.GA4_WEB_STREAM_IDS,
    NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN: process.env.NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN,
    NEXT_PUBLIC_ROLLBAR_TIME_WINDOW_MIN: +(process.env.NEXT_PUBLIC_TIME_WINDOW_MIN || 0),
    NEXT_PUBLIC_ROLLBAR_ENV: process.env.NEXT_PUBLIC_ROLLBAR_ENV,
    NEXT_PUBLIC_FIREBASE_CONFIG: process.env.NEXT_PUBLIC_FIREBASE_CONFIG,
    NEXT_PUBLIC_FIREBASE_MESSAGING_VAPID_KEY: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_VAPID_KEY,
    NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
    NEXT_PUBLIC_URL: process.env.NEXT_PUBLIC_URL,
    NEXT_PUBLIC_GA_TRACKING_ID: process.env.NEXT_PUBLIC_GA_TRACKING_ID,
    NEXT_PUBLIC_GOOGLE_ANALYTICS: process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS,
    NEXT_PUBLIC_FACEBOOK_PIXEL_ID: process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID,
    NEXT_PUBLIC_FORCE_SUBDOMAIN: process.env.NEXT_PUBLIC_FORCE_SUBDOMAIN,
    NEXT_PUBLIC_API_REST_BASE_URL: process.env.NEXT_PUBLIC_API_REST_BASE_URL,
  },
});

export type Environment = typeof env;
